import { read as readMat } from 'mat-for-js';
import * as turf from '@turf/turf';
import { reprojectPoint } from './reprojection';

const MATLAB_DATENUM_EPOCH_OFFSET_DAYS = 719529; // days from year 0000-01-01 to 1970-01-01
const MS_PER_DAY = 86400000;

/**
 * @function unwrapScalarStruct
 * @description Per mat-for-js's documented behavior, *escalar* MATLAB structs convert directly
 * to JS objects with fields accessible as `obj.field`.
 * **Called by:
 * @see {loadShorelineSMatfile}
 * @memberof module:ShoreS
 * @returns {object}
 */
const unwrapScalarStruct = (raw) => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (Array.isArray(raw) && Array.isArray(raw[1]) && raw[1][0] && typeof raw[1][0] === 'object') {
    return raw[1][0];
  }
  return raw || {};
};

/**
 * @function loadShorelineSMatfile
 * @description Loads a ShorelineS `.mat` (MATLAB v5) file and returns the `S` (model
 * state/settings) and `O` (output results) structs as plain JS objects.
 * @param {ArrayBuffer} arrayBuffer
 * **Called by:
 * @see {readCoastlineFromMatfile}
 * @memberof module:ShoreS
 * @returns {{modelState: object, output: object, metadata: object}}
 */
export const loadShorelineSMatfile = (arrayBuffer) => {
  const parsed = readMat(arrayBuffer);
  const data = (parsed && parsed.data) || {};
  return {
    modelState: unwrapScalarStruct(data.S),
    output: unwrapScalarStruct(data.O),
    metadata: { header: parsed && parsed.header },
  };
};

// Collapses singleton dimensions. mat-for-js
// already flattens 1xn/nx1 vectors to plain 1D arrays, but this defends
// against any remaining wrapper dimensions (e.g. shape (1, n) read as
// [[...]], or a (n, 1) array of length-1 rows).
const squeeze = (value) => {
  if (!Array.isArray(value)) return value;
  if (value.length === 1) return squeeze(value[0]);
  if (value.length > 0 && value.every((el) => Array.isArray(el) && el.length === 1)) {
    return value.map((el) => squeeze(el[0]));
  }
  return value;
};

const arraySize = (value) => {
  if (!Array.isArray(value)) return value === undefined || value === null ? 0 : 1;
  return value.reduce((total, el) => total + arraySize(el), 0);
};

/**
 * @function extractFromMatlabArray
 * @description Reads a field off a parsed MATLAB struct, squeezes singleton dimensions,
 * and falls back to `defaultValue` if the field is missing/empty.
 * @param {object} data
 * @param {string} fieldName
 * @param {*} [defaultValue]
 * **Called by:
 * @see {extractCoastlineData}
 * @see {extractTimeVector}
 * @memberof module:ShoreS
 * @returns {*}
 */
export const extractFromMatlabArray = (data, fieldName, defaultValue = []) => {
  const raw = data ? data[fieldName] : undefined;
  if (raw === undefined || raw === null) return defaultValue;
  const squeezed = squeeze(raw);
  return arraySize(squeezed) > 0 ? squeezed : defaultValue;
};

const flattenDeep = (value) =>
  Array.isArray(value) ? value.flatMap(flattenDeep) : [value];

/**
 * @function matlabDatenumToDatetime
 * @description Converts MATLAB datenums (days counted since year 0000-01-01) to JS Dates
 * by shifting them to the Unix epoch. Sub-day precision (fractional
 * datenums) is preserved since the conversion multiplies straight into
 * milliseconds.
 * @param {Array} datenumArray
 * **Called by:
 * @see {extractTimeVector}
 * @memberof module:ShoreS
 * @returns {Date[]}
 */
export const matlabDatenumToDatetime = (datenumArray) =>
  flattenDeep(Array.isArray(datenumArray) ? datenumArray : [datenumArray]).map(
    (datenum) => new Date((datenum - MATLAB_DATENUM_EPOCH_OFFSET_DAYS) * MS_PER_DAY),
  );

/**
 * @function extractCoastlineData
 * @description Extracts the modeled coastline x/y coordinate arrays from the output struct.
 * @param {object} modeledData - The model `output` (O) struct.
 * **Called by:
 * @see {readCoastlineFromMatfile}
 * @memberof module:ShoreS
 * @returns {{x: number[][], y: number[][]}} Each shaped (nPoints, nTimesteps).
 */
export const extractCoastlineData = (modeledData) => ({
  x: extractFromMatlabArray(modeledData, 'x'),
  y: extractFromMatlabArray(modeledData, 'y'),
});

/**
 * @function extractTimeVector
 * @description Extracts the model's time vector as JS Dates. Throws if the model has no
 * reference start date (`reftime`); callers should treat this as the
 * "no temporal data" case, not a fatal error.
 * @param {object} modelOutput - The `O` struct.
 * @param {object} modelState - The `S` struct.
 * **Called by:
 * @see {readCoastlineFromMatfile}
 * @memberof module:ShoreS
 * @returns {Date[]}
 */
export const extractTimeVector = (modelOutput, modelState) => {
  const startDate = modelState ? modelState.reftime : undefined;
  if (startDate === undefined || startDate === null) {
    throw new Error('Time vectors not based on a start date are still in development');
  }
  const rawTimeVector = extractFromMatlabArray(modelOutput, 'timenum');
  return matlabDatenumToDatetime(rawTimeVector);
};

/**
 * @function makeIndexPseudoTimeVector
 * @description Builds a per-timestep index when the model carries no calendar dates
 * (`reftime` missing), so the modeled coastlines still have data to
 * be grouped/visualized by, instead of failing.
 * @param {number} nTimesteps
 * **Called by:
 * @see {readCoastlineFromMatfile}
 * @memberof module:ShoreS
 * @returns {number[]}
 */
export const makeIndexPseudoTimeVector = (nTimesteps) =>
  Array.from({ length: nTimesteps }, (_, i) => i);

/**
 * @function makeTimeIndexedCoastline
 * @description Groups the modeled coastline into one entry per timestep, each carrying
 * its `time` and the list of {x, y} points at that timestep.
 * @param {{x: number[][], y: number[][]}} coastlineCoordsDict
 * @param {Array<Date|number>} timeVector
 * **Called by:
 * @see {readCoastlineFromMatfile}
 * @memberof module:ShoreS
 * @returns {Array<{time: Date|number, points: Array<{x:number,y:number}>}>}
 */
export const makeTimeIndexedCoastline = (coastlineCoordsDict, timeVector) => {
  const { x, y } = coastlineCoordsDict;
  if (!Array.isArray(x) || !Array.isArray(x[0])) {
    throw new Error('Expected 2D x/y coastline arrays shaped (nPoints, nTimesteps).');
  }
  if (x.length !== y.length) {
    throw new Error(`x and y coastline arrays must have the same number of points (got ${x.length} vs ${y.length}).`);
  }

  const nTimesteps = x[0].length;
  if (timeVector.length !== nTimesteps) {
    throw new Error(`Time vector length (${timeVector.length}) must match number of timesteps (${nTimesteps}).`);
  }

  const coastlineByTime = [];
  for (let t = 0; t < nTimesteps; t += 1) {
    const points = x.map((row, p) => ({ x: row[t], y: y[p][t] }));
    coastlineByTime.push({ time: timeVector[t], points });
  }
  return coastlineByTime;
};

export const MIN_POINTS_OF_COASTLINE_TO_KEEP = 10;

const isNullish = (v) => v === null || v === undefined || Number.isNaN(v);

/**
 * @function dropXyNullRows
 * @description Removes points with neither x nor y defined from each timestep.
 * @param {Array<{time, points}>} coastlineByTime
 * **Called by:
 * @see {prepareGeoData}
 * @memberof module:ShoreS
 * @returns {Array<{time, points}>}
 */
export const dropXyNullRows = (coastlineByTime) =>
  coastlineByTime.map((entry) => ({
    ...entry,
    points: entry.points.filter((p) => !(isNullish(p.x) && isNullish(p.y))),
  }));

/**
 * @function dropShortShorelineDates
 * @description Drops timesteps whose coastline has too few points.
 * @param {Array<{time, points}>} coastlineByTime
 * @param {number} [nPointsThreshold]
 * **Called by:
 * @see {prepareGeoData}
 * @memberof module:ShoreS
 * @returns {Array<{time, points}>}
 */
export const dropShortShorelineDates = (coastlineByTime, nPointsThreshold = MIN_POINTS_OF_COASTLINE_TO_KEEP) =>
  coastlineByTime.filter((entry) => entry.points.length > nPointsThreshold);

/**
 * @function prepareGeoData
 * @description Cleans the coastline data: drops null points, then too-short timesteps.
 * @param {Array<{time, points}>} coastlineByTime
 * **Called by:
 * @see {getModeledCoastlinesGeoJSON}
 * @see {getModeledCoastlinesDisplayGeoJSON}
 * @memberof module:ShoreS
 * @returns {Array<{time, points}>}
 */
export const prepareGeoData = (coastlineByTime) =>
  dropShortShorelineDates(dropXyNullRows(coastlineByTime));

/**
 * @function getModeledCoastlinesGeoJSON
 * @description Groups the (already filtered) per-timestep points into a GeoJSON
 * LineString per timestep, in the original CRS supplied by the user.
 * @param {Array<{time, points}>} coastlineByTime
 * @param {string} crs
 * **Called by:
 * @see {parseShoreSModelFile}
 * @memberof module:ShoreS
 * @returns {GeoJSON.FeatureCollection}
 */
export const getModeledCoastlinesGeoJSON = (coastlineByTime, crs) => {
  const prepared = prepareGeoData(coastlineByTime);
  const features = prepared.map((entry) => ({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: entry.points.map((p) => [p.x, p.y]),
    },
    properties: {
      time: entry.time instanceof Date ? entry.time.toISOString() : entry.time,
    },
  }));
  return {
    type: 'FeatureCollection',
    crs: { type: 'name', properties: { name: crs } },
    features,
  };
};

/** A point that can actually be drawn: both coordinates are finite numbers. */
const isPlottablePoint = (p) => Number.isFinite(p?.x) && Number.isFinite(p?.y);

/**
 * @function splitPointsOnGaps
 * @description Splits a timestep's points into the runs of consecutive plottable points, so
 * every discarded point becomes a break instead of a segment bridging the gap.
 * Runs shorter than two points are dropped, since a LineString needs at least
 * two positions.
 * @param {Array<{x:number,y:number}>} points
 * **Called by:
 * @see {getModeledCoastlinesDisplayGeoJSON}
 * @memberof module:ShoreS
 * @returns {Array<Array<Array<number>>>} One coordinate array per segment, each coordinate an `[x, y]` pair.
 */
export const splitPointsOnGaps = (points) => {
  const segments = [];
  let current = [];

  points.forEach((p) => {
    if (isPlottablePoint(p)) {
      current.push([p.x, p.y]);
      return;
    }
    if (current.length > 1) segments.push(current);
    current = [];
  });
  if (current.length > 1) segments.push(current);

  return segments;
};

/**
 * @function getModeledCoastlinesDisplayGeoJSON
 * @description Display-only counterpart of `getModeledCoastlinesGeoJSON`: same timesteps, but
 * each gap-separated run of points becomes its own LineString Feature, so the
 * map draws a broken line instead of connecting across a discarded point.
 *
 * @param {Array<{time, points}>} coastlineByTime
 * @param {string} crs
 * **Called by:
 * @see {parseShoreSModelFile}
 * @memberof module:ShoreS
 * @returns {GeoJSON.FeatureCollection} One LineString Feature per segment.
 */
export const getModeledCoastlinesDisplayGeoJSON = (coastlineByTime, crs) => {
  const features = [];

  // `prepareGeoData` strips the gap points, so it can only decide *which*
  // timesteps are kept — the segmentation below has to run on the original
  // points, which still carry the gaps.
  const isKeptTimestep = (entry) => prepareGeoData([entry]).length > 0;

  coastlineByTime.filter(isKeptTimestep).forEach((entry) => {
    const time = entry.time instanceof Date ? entry.time.toISOString() : entry.time;
    splitPointsOnGaps(entry.points).forEach((coordinates, segment) => {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates },
        properties: { time, segment },
      });
    });
  });

  return {
    type: 'FeatureCollection',
    crs: { type: 'name', properties: { name: crs } },
    features,
  };
};

export const ROI_PADDING_RATIO = 0.1;

/**
 * @function padBBox
 * @description Expands a WGS84 bounding box by `ratio` of each axis' own span, on every side,
 * so the modeled data doesn't sit flush against the ROI border. Each axis is
 * padded independently, which keeps the box's proportions.
 * @param {Array<number>} bbox - `[west, south, east, north]`.
 * @param {number} [ratio]
 * **Called by:
 * @see {deriveRoiBBoxInWGS84}
 * @memberof module:ShoreS
 * @returns {Array<number>} The padded `[west, south, east, north]` bbox, clamped to valid WGS84 ranges.
 */
export const padBBox = ([west, south, east, north], ratio = ROI_PADDING_RATIO) => {
  const padX = (east - west) * ratio;
  const padY = (north - south) * ratio;

  return [
    Math.max(west - padX, -180),
    Math.max(south - padY, -90),
    Math.min(east + padX, 180),
    Math.min(north + padY, 90),
  ];
};

/**
 * @function deriveRoiBBoxInWGS84
 * @description Reprojects every coordinate of a FeatureCollection to EPSG:4326 and returns
 * its **tight** bounding box. Callers that want the padded AOI apply `padBBox`
 * themselves, see `modelFile.js`, which keeps the tight box for the reference
 * extraction ROI and the padded one for the imagery search.
 * @param {GeoJSON.FeatureCollection} featureCollection
 * @param {string} crs - The FeatureCollection's current CRS.
 * **Called by:
 * @see {parseShoreSModelFile}
 * @memberof module:ShoreS
 * @returns {Array<number>} `[west, south, east, north]`
 */
export const deriveRoiBBoxInWGS84 = (featureCollection, crs) => {
  if (!featureCollection.features.length) return null;
  const reprojected = {
    ...featureCollection,
    features: featureCollection.features.map((feature) => ({
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: feature.geometry.coordinates.map((coord) => reprojectPoint(coord, crs, 'EPSG:4326')),
      },
    })),
  };
  return turf.bbox(reprojected);
};

/**
 * @function readCoastlineFromMatfile
 * @description Reads an uploaded ShorelineS `.mat` into the per-timestep coastline structure
 * shared with the CSV path. A model without a reference start date (`reftime`)
 * is not an error, it just carries no calendar dates, so the timesteps are
 * indexed instead.
 * @param {ArrayBuffer} arrayBuffer
 * **Called by:
 * @see {parseShoreSModelFile}
 * @memberof module:ShoreS
 * @returns {{coastlineByTime: Array<{time: Date|number, points: Array<{x:number,y:number}>}>,
 *            hasTemporalData: boolean}}
 */
export const readCoastlineFromMatfile = (arrayBuffer) => {
  const { modelState, output } = loadShorelineSMatfile(arrayBuffer);
  const coastlineData = extractCoastlineData(output);

  let hasTemporalData = true;
  let timeVector;
  try {
    timeVector = extractTimeVector(output, modelState);
  } catch (e) {
    hasTemporalData = false;
    const nTimesteps = Array.isArray(coastlineData.x[0]) ? coastlineData.x[0].length : 1;
    timeVector = makeIndexPseudoTimeVector(nTimesteps);
  }

  return { coastlineByTime: makeTimeIndexedCoastline(coastlineData, timeVector), hasTemporalData };
};
